import { useState } from "react";
import { useNavigate } from "react-router";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
  DialogCTA,
} from "../../components/modal/Dialog";
import { InputLabel, InputContainer } from "@sellar-io/components";
import { InputText } from "@sellar-io/components";
import { Icon } from "../../components";
import { Alert } from "../../components/alert/Alert";
import { useCustomerZonesCreate } from "../../hooks/api/v2/customerZones/useCustomerZonesCreate";
import { useCustomerZonePostcodesAdd } from "../../hooks/api/v2/customerZones/useCustomerZonePostcodesAdd";
import { useCustomerZonePostcodesMove } from "../../hooks/api/v2/customerZones/useCustomerZonePostcodesMove";
import { useCustomerZonePostcodesCoverage } from "../../hooks/api/v2/customerZones/useCustomerZonePostcodesCoverage";
import { expandPostcodeRange } from "../../utils/expandPostcodeRange";
import * as ROUTES from "../../constants/routes";
import { apiV2AssertError } from "~/hooks/api/v2/apiV2Request";
import {
  Button,
  DataButton,
  SingleFieldDialog,
  SingleFieldDialogHeader,
  SingleFieldDialogCTA,
} from "@sellar-io/components";
import { checkPostcodeValidCharacters } from "@sellar-io/common";

export const DeliveryZoneCreateDialog = ({
  active,
  setActive,
}: {
  active: boolean;
  setActive: (active: boolean) => void;
}) => {
  return (
    <Dialog active={active} onDismiss={() => setActive(false)}>
      <DialogNavigation
        leftIcon="CHEVRON_DOWN"
        onClickLeftIcon={() => setActive(false)}
        title="New delivery zone"
      />
      {active && <Content setActive={setActive} />}
    </Dialog>
  );
};

type ConflictState = {
  // Postcodes that exist exactly in another zone (can be moved)
  exactMatchPostcodes: string[];
  // Postcodes covered by a broader postcode in another zone (cannot be moved)
  coverageMatchPostcodes: Array<{
    postcode: string;
    coveringPostcode: string;
  }>;
  nonConflictingPostcodes: string[];
};

const Content = ({ setActive }: { setActive: (active: boolean) => void }) => {
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [postcodeDraft, setPostcodeDraft] = useState("");
  const [postcodes, setPostcodes] = useState<string[]>([]);
  const [invalidPostcode, setInvalidPostcode] = useState(false);
  const [postcodeAddError, setPostcodeAddError] = useState<string | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [conflict, setConflict] = useState<ConflictState | null>(null);
  const [allConflictDialogActive, setAllConflictDialogActive] = useState(false);
  const [createdZoneId, setCreatedZoneId] = useState<number | null>(null);

  const customerZoneCreate = useCustomerZonesCreate();
  const coverageMutation = useCustomerZonePostcodesCoverage();
  const addPostcodesMutation = useCustomerZonePostcodesAdd(createdZoneId ?? 0);
  const movePostcodesMutation = useCustomerZonePostcodesMove(
    createdZoneId ?? 0,
  );

  const handleAddPostcode = () => {
    setInvalidPostcode(false);
    setPostcodeAddError(null);
    const postcode = postcodeDraft;
    if (!postcode) return;

    const expandedPostcodes = expandPostcodeRange(postcode);

    if (expandedPostcodes.some((pc) => !checkPostcodeValidCharacters(pc))) {
      return setInvalidPostcode(true);
    }

    const postcodesToAdd = expandedPostcodes.map((pc) => pc.toUpperCase());

    // Deduplicate against already-added postcodes
    const newPostcodes = postcodesToAdd.filter((pc) => !postcodes.includes(pc));

    if (newPostcodes.length === 0) {
      setPostcodeAddError("Postcode already added");
      return;
    }

    setPostcodes((prev) => [...prev, ...newPostcodes]);
    setPostcodeDraft("");
  };

  const handleRemovePostcode = (postcode: string) => {
    setPostcodes((prev) => prev.filter((p) => p !== postcode));
  };

  const navigateToZone = (zoneId: number) => {
    setActive(false);
    navigate(ROUTES.DELIVERY_ZONE.href({ deliveryZoneId: zoneId }));
  };

  const createZoneAndAddPostcodes = async (postcodesToAdd: string[]) => {
    const zoneId =
      createdZoneId ??
      (
        await customerZoneCreate.mutateAsync({
          type: "direct",
          name: name.trim() || "New delivery zone",
          default: false,
          PriceTierId: null,
          AvailabilityTierId: null,
          OrderRulesId: null,
        })
      ).id;
    setCreatedZoneId(zoneId);
    if (postcodesToAdd.length > 0) {
      await addPostcodesMutation.mutateAsync({
        customerZoneId: zoneId,
        postcodeComponents: postcodesToAdd,
      });
    }
    return zoneId;
  };

  const handleCreate = async () => {
    setCreateError(null);
    setConflict(null);
    setIsSubmitting(true);

    try {
      if (postcodes.length === 0) {
        const zoneId = await createZoneAndAddPostcodes([]);
        navigateToZone(zoneId);
        return;
      }

      const coverageResult = await coverageMutation.mutateAsync({
        postcodeComponents: postcodes,
      });

      const coveredItems = coverageResult.coverage.filter(
        (c) => c.isCoveredByCustomerZone,
      );
      const nonConflicting = coverageResult.coverage
        .filter((c) => !c.isCoveredByCustomerZone)
        .map((c) => c.postcodeComponent);

      // Separate exact matches (can be moved) from coverage matches (cannot be moved)
      const exactMatchPostcodes = coveredItems
        .filter((c) => c.isExactMatch)
        .map((c) => c.postcodeComponent);
      const coverageMatchPostcodes = coveredItems
        .filter((c) => !c.isExactMatch && c.coveringPostcodeComponent)
        .map((c) => ({
          postcode: c.postcodeComponent,
          coveringPostcode: c.coveringPostcodeComponent!,
        }));

      const hasAnyConflict =
        exactMatchPostcodes.length > 0 || coverageMatchPostcodes.length > 0;

      if (!hasAnyConflict) {
        const zoneId = await createZoneAndAddPostcodes(postcodes);
        navigateToZone(zoneId);
        return;
      }

      // If only coverage conflicts (no exact matches), can't create zone with those postcodes
      if (
        exactMatchPostcodes.length === 0 &&
        coverageMatchPostcodes.length > 0 &&
        nonConflicting.length === 0
      ) {
        setAllConflictDialogActive(true);
        return;
      }

      setConflict({
        exactMatchPostcodes,
        coverageMatchPostcodes,
        nonConflictingPostcodes: nonConflicting,
      });
    } catch (error) {
      setCreateError(
        apiV2AssertError(error)?.response?.data.message ??
          "Unable to create delivery zone",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMovePostcodes = async () => {
    if (!conflict) return;
    setCreateError(null);
    setIsSubmitting(true);
    try {
      const zoneId = await createZoneAndAddPostcodes(
        conflict.nonConflictingPostcodes,
      );
      await movePostcodesMutation.mutateAsync({
        customerZoneId: zoneId,
        postcodeComponents: conflict.exactMatchPostcodes,
      });
      navigateToZone(zoneId);
    } catch (error) {
      setCreateError(
        apiV2AssertError(error)?.response?.data.message ??
          "Failed to create delivery zone",
      );
      setIsSubmitting(false);
    }
  };

  const handleDontMovePostcodes = async () => {
    if (!conflict) return;
    setCreateError(null);
    setIsSubmitting(true);
    try {
      const zoneId = await createZoneAndAddPostcodes(
        conflict.nonConflictingPostcodes,
      );
      navigateToZone(zoneId);
    } catch (error) {
      setCreateError(
        apiV2AssertError(error)?.response?.data.message ??
          "Failed to create delivery zone",
      );
      setIsSubmitting(false);
    }
  };

  const handleAllConflictDismiss = () => {
    setAllConflictDialogActive(false);
    setActive(false);
  };

  const canCreate = name.trim().length > 0;
  const hasConflict = conflict !== null;
  const zoneName = name.trim() || "New delivery zone";

  return (
    <>
      <DialogContent className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <InputLabel>Name</InputLabel>
          <InputContainer>
            <InputText
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter a name for this delivery zone"
              required
            />
          </InputContainer>
        </div>

        <div className="flex flex-col gap-2">
          <InputLabel>Postcodes</InputLabel>
          <p className="text-sm text-zinc-500">
            Any customers whose postcode is described below will fall into this
            delivery zone. You can add as many postcodes as you like now, as
            well as after you've created this delivery zone.
          </p>
          <div className="flex gap-4">
            <InputContainer className="w-full">
              <InputText
                className="w-full"
                value={postcodeDraft}
                onChange={(e) => {
                  setPostcodeAddError(null);
                  setPostcodeDraft(e.target.value);
                }}
                placeholder="e.g. BS1 or S1-9"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleAddPostcode();
                  }
                }}
              />
            </InputContainer>
            <Button disabled={!postcodeDraft} onClick={handleAddPostcode}>
              <Icon icon="PLUS" />
              Add
            </Button>
          </div>

          {postcodeAddError && (
            <p className="text-sm text-red-700">{postcodeAddError}</p>
          )}

          {invalidPostcode && (
            <Alert
              title="Unable to save postcode"
              message="Please enter either a postcode area (such as BS), postcode district (such as BS1) or postcode sector (such as BS1 6)."
            />
          )}

          <div className="flex flex-wrap gap-2">
            {postcodes.map((postcode) => (
              <DataButton
                key={postcode}
                border={true}
                onClick={() => handleRemovePostcode(postcode)}
              >
                <span className="flex items-center gap-2">
                  {postcode}
                  <Icon icon="CLOSE" size={0.6} />
                </span>
              </DataButton>
            ))}
          </div>
        </div>

        {/* Coverage match errors - cannot be moved */}
        {hasConflict && conflict.coverageMatchPostcodes.length > 0 && (
          <Alert
            type="error"
            title={`${conflict!.coverageMatchPostcodes.map((c) => c.postcode).join(", ")} ${conflict!.coverageMatchPostcodes.length === 1 ? "is" : "are"} covered by a broader postcode in another delivery zone`}
            message={conflict!.coverageMatchPostcodes.map((c) => (
              <span key={c.postcode}>
                {c.postcode} is covered by {c.coveringPostcode} in another zone.
                To add {c.postcode}, you must first remove {c.coveringPostcode}{" "}
                from the other zone.
              </span>
            ))}
          />
        )}

        {/* Exact match warnings - can be moved */}
        {hasConflict && conflict!.exactMatchPostcodes.length > 0 && (
          <Alert
            type="warning"
            title={`${conflict!.exactMatchPostcodes.join(", ")} ${conflict!.exactMatchPostcodes.length === 1 ? "is" : "are"} already listed in an existing delivery zone`}
            message={`You can move ${conflict!.exactMatchPostcodes.length === 1 ? "this postcode" : "these postcodes"} to your new ${zoneName} delivery zone`}
          >
            <div className="flex gap-2">
              <Button
                color="secondary"
                className="border border-orange-800 bg-transparent text-orange-800"
                loading={isSubmitting}
                disabled={isSubmitting}
                onClick={handleMovePostcodes}
              >
                Move postcodes
              </Button>
              <Button
                color="transparent"
                className="text-orange-800"
                disabled={isSubmitting}
                onClick={handleDontMovePostcodes}
              >
                Don't move these postcodes
              </Button>
            </div>
          </Alert>
        )}

        {createError && <Alert type="error" message={createError} />}
      </DialogContent>

      <DialogCTA>
        <Button
          disabled={!canCreate || isSubmitting || hasConflict}
          loading={isSubmitting}
          onClick={handleCreate}
        >
          Create
        </Button>
      </DialogCTA>

      <SingleFieldDialog
        active={allConflictDialogActive}
        onDismiss={handleAllConflictDismiss}
        size="large"
      >
        <SingleFieldDialogHeader
          title={`'${zoneName}' delivery zone could not be created`}
          description={`All postcodes are already listed in existing delivery zones, so '${zoneName}' could not be created with any postcodes.`}
          icon="ALERT_FILLED"
          iconClassName="bg-red-50 text-red-600"
        />
        <SingleFieldDialogCTA>
          <Button onClick={handleAllConflictDismiss}>Okay</Button>
        </SingleFieldDialogCTA>
      </SingleFieldDialog>
    </>
  );
};
